import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import { Flex } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useRoles } from "@/hooks/useRoles";

import { NoData } from "@/components/common/section-components/NoData";
import { PermissionCode } from "@/constants/permissions";
import { RequestPoliciesActions } from "@/components/common/section-components/action/RequestPoliciesActions";
import { RequestPolicy } from "./schema/request-policies.schema";
import { RPHeaderElements } from "./schema/header-elements";

import { fetchRequestPoliciesHandler, handleDeletePoliciesHandler } from "./schema/handler-functions";
import { formatCurrency } from "@/utils/format-currency";
import { usePolling } from "@/hooks/usePolling";

export const RequestPoliciesPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const navigate = useNavigate();
  const [selection, setSelection] = useState<string[]>([]);

  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const [loadingPolicies, setLoadingPolicies] = useState<boolean>(false);
  const [loadingDeletePolicies, setLoadingDeletePolicies] =
    useState<boolean>(false);

  const [policies, setPolicies] = useState<RequestPolicy[] | null>([]);
  const { userPermissions, fetchPermissions  } = useRoles();
  const fetchPolicies = async (showLoad = true) => {
    await fetchRequestPoliciesHandler({
      setLoadingPolicies,
      setPolicies,
      navigate,
      fetchPermissions,
      showLoad,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) fetchPolicies();
  }, [isActive]);

  usePolling({
    callback: () => fetchPolicies(false),
    delay: 10000,
    isActive,
  });

  const handleAddRole = () => {
    navigate(AppRoutes.REQUEST_POLICIES_ADD);
  };

  const handleDeletePolicies = async () => {
    handleDeletePoliciesHandler({
      selection,
      setLoadingDeletePolicies,
      setOpenConfirm,
      fetchPolicies,
      setSelection,
      navigate,
      fetchPermissions,
    });
  };

  const handleEdit = (el: RequestPolicy) => {
    navigate(AppRoutes.REQUEST_POLICIES_EDIT.replace(":id", String(el?.id)));
  };

  const getActionElements = (item: RequestPolicy) => (
    <RequestPoliciesActions handleEdit={handleEdit} item={item} />
  );

  const hasSelection = selection.length > 0;
  const indeterminate = Boolean(
    hasSelection && policies && selection.length < policies?.length,
  );

  const getValorByClase = (valor: string, clase: string) => {
    if (clase === "Tope diario") return `$ ${formatCurrency(Number(valor))}`;

    if (clase === "Rango semanal") return valor?.replaceAll(",", ", ");

    if (clase === "Horario") return valor?.replace("-", ` - `);
  };

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.BUSINESS_POLICY && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="roles-page">
      <SectionHeader
        title={`Políticas de negocio`}
        addButtonLabel="Agregar"
        deleteButtonLabel="Eliminar"
        handleAdd={handleAddRole}
        handleDelete={() => setOpenConfirm(true)}
        disableDelete={!selection?.length}
        loading={loadingPolicies || loadingDeletePolicies}
        showButtons={hasWriteAccess}
      />
      {/* Tabla */}
      <Flex flexDir="column" marginTop="30px">
        {Boolean(!loadingPolicies && !policies?.length) && <NoData />}
        {Boolean(!loadingPolicies && policies?.length) && (
          <StandardTable
            indeterminate={indeterminate}
            items={
              policies?.map((pol: RequestPolicy) => ({
                ...pol,
                aplicaA: pol?.aplicaA?.join(", "),
                valor: getValorByClase(pol?.valor, pol?.clase),
                activo: pol?.activo ? "Activa" : "Inactiva",
              })) ?? []
            }
            selection={selection}
            setSelection={setSelection}
            headers={RPHeaderElements}
            noActions={!hasWriteAccess}
            noSelection={!hasWriteAccess}
            actionElements={getActionElements}
            actionsHeaderLabel="Edición"
            multipleSelection
          />
        )}
      </Flex>
      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirm}
          handleAccept={handleDeletePolicies}
          loadingAccept={loadingDeletePolicies}
          title="Eliminar políticas de negocio"
          subtitle="¿Está seguro de que desea realizar esta acción?"
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
